import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { LayoutContext } from "contexts/LayoutContext/LayoutContext";
import { MainSurveyStore } from "modules/MainSurveyEditor/hooks/useMainSurvey";
import { SurveyBlocks } from "modules/MainSurveyEditor/surveyBlocks";
import { SupportingInfo } from "../supportingInfo";
import { relatedSupportingInfoClient } from "../client/relatedSupportingInfoClient";
import { useSupportingInfoData } from "./useSupportingInfoData";
import { computeSharedSupportingInfos } from "../helpers/bulkSupportingInfo";
import { RelatedSupportingInfoController } from "../components/BlockFormSupportingInfo/RelatedSupportingInfosView";

type IdMap = Record<string, number | string>;

export const useBulkRelatedSupportingInfo = (
  selectedBlocks: SurveyBlocks.Block[],
  mainSurveyStore: Pick<MainSurveyStore, "setIsSaving" | "handleSurveySaved"> & { genericError?: (m: string) => void },
  onSaved?: () => void,
): RelatedSupportingInfoController & { hasExtra: boolean } => {
  const layout = useContext(LayoutContext);
  const genericError = mainSurveyStore.genericError ?? layout.genericError;
  const { setIsSaving, handleSurveySaved } = mainSurveyStore;

  const { supportingInfos: infoOptions, isLoadingInfos } = useSupportingInfoData({ limit: 1000, offset: 0 });

  const [relatedInfos, setRelatedInfos] = useState<SupportingInfo.PartialRelatedSupportingInfo[]>([]);
  const [idMaps, setIdMaps] = useState<IdMap[]>([]);
  const [hasExtra, setHasExtra] = useState<boolean>(false);

  const selectionKey = useMemo(() => selectedBlocks.map((b) => b.id).join(","), [selectedBlocks]);

  useEffect(() => {
    const { infos, idMaps: maps, hasExtra: extra } = computeSharedSupportingInfos(
      selectedBlocks.map((b) => ({ id: b.id, relatedSupportingInformation: b.relatedSupportingInformation ?? [] })),
    );
    setRelatedInfos(infos);
    setIdMaps(maps);
    setHasExtra(extra);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey]);

  const addSupportingInfo = useCallback(() => {
    setRelatedInfos((old) => [...old, { supportingInformation: {} }]);
    setIdMaps((old) => [...old, {}]);
  }, []);

  const handleSelectSupportingInfo = useCallback(
    async (index: number, infoId: number | string) => {
      const map = idMaps[index] ?? {};
      const selected = infoOptions.find((i) => String(i.id) === String(infoId));
      setIsSaving(true);

      const results = await Promise.allSettled(
        selectedBlocks.map(async (b) => {
          const existingId = map[String(b.id)];
          if (existingId !== undefined) {
            await relatedSupportingInfoClient.updateRelatedSupportingInfo(b.surveyId, b.id, existingId, {
              supportingInformation: infoId,
            });
            return { blockId: String(b.id), relatedId: existingId };
          }
          const res = await relatedSupportingInfoClient.addSupportingInfoToBlock(b.surveyId, b.id, infoId);
          return { blockId: String(b.id), relatedId: res.data.id! };
        }),
      );

      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed) genericError(`Failed to update ${failed} of ${selectedBlocks.length} cells.`);

      const newMap: IdMap = {};
      results.forEach((r) => {
        if (r.status === "fulfilled") newMap[r.value.blockId] = r.value.relatedId;
      });
      // Merge (not replace) so a cell whose request failed keeps its existing
      // relatedId — that attachment still exists on the server.
      setIdMaps((old) => old.map((m, i) => (i === index ? { ...m, ...newMap } : m)));
      setRelatedInfos((old) =>
        old.map((info, i) => (i === index && selected ? { ...info, supportingInformation: selected } : info)),
      );
      handleSurveySaved();
      onSaved?.();
    },
    [idMaps, infoOptions, selectedBlocks, setIsSaving, handleSurveySaved, genericError, onSaved],
  );

  const handleDeleteInfo = useCallback(
    async (index: number) => {
      const map = idMaps[index] ?? {};
      setRelatedInfos((old) => old.filter((_, i) => i !== index));
      setIdMaps((old) => old.filter((_, i) => i !== index));

      const entries = selectedBlocks
        .map((b) => ({ block: b, relatedId: map[String(b.id)] }))
        .filter((e) => e.relatedId !== undefined);
      if (!entries.length) return;

      setIsSaving(true);
      const results = await Promise.allSettled(
        entries.map((e) =>
          relatedSupportingInfoClient.deleteSupportingInfoFromBlock(e.block.surveyId, e.block.id, e.relatedId!),
        ),
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed) genericError(`Failed to remove from ${failed} of ${entries.length} cells.`);
      handleSurveySaved();
      onSaved?.();
    },
    [idMaps, selectedBlocks, setIsSaving, handleSurveySaved, genericError, onSaved],
  );

  const noop = useCallback(() => undefined, []);

  return {
    relatedInfos,
    infoOptions,
    isLoadingInfoOptions: isLoadingInfos,
    addSupportingInfo,
    handleSelectSupportingInfo,
    handleContentUpdate: noop,
    handleTabNameUpdate: noop,
    handleDeleteInfo,
    setIsSaving,
    hasExtra,
  };
};
